/* =========================================================================
   LEAD DELIVERY — NETLIFY FORMS
   =========================================================================

   Submissions go to Netlify Forms, which stores them in the site dashboard
   and emails them onward. No API key, no third-party script, no runtime
   dependency, and nothing secret lives in this bundle.

   THREE PIECES MUST AGREE OR SUBMISSIONS SILENTLY 404:

     1. public/index.html holds a hidden static form with the same `name`,
        the same field `name` attributes, and `data-netlify="true"`.
        Netlify's build-time parser only reads static HTML — it never sees a
        React-rendered form — so without that stub the form does not exist
        as far as Netlify is concerned. This is the single most common way
        Netlify Forms "mysteriously" stops working.

     2. The live <form> below renders a hidden `form-name` input, because
        the POST body must name the form.

     3. NETLIFY_FORM_NAME below.

   Change the form name or add a field, and you must update all three.

   SPAM: a honeypot field (`bot-field`) is declared on the static form via
   netlify-honeypot and submitted empty by real users. Bots that fill every
   input get dropped by Netlify before they reach the inbox. If spam still
   gets through, enable reCAPTCHA in the Netlify UI rather than here.

   EMAIL DESTINATION IS NOT SET IN CODE. It is a per-site setting:
   Netlify dashboard > Forms > Form notifications > add "Email notification",
   pointed at info@yourmegaservices.com. Until that is configured the
   submissions are still captured in the dashboard but nobody is alerted.

   LOCAL DEV: Netlify Forms only exist on a deployed Netlify site. Running
   `npm start` and submitting will fail, because there is no Netlify layer in
   front of the CRA dev server. Test against a deploy preview, or use
   `netlify dev`. A local failure here is expected, not a bug.
   ========================================================================= */

import { useRef, useState } from "react";
import { LuPhone, LuMail, LuLoader, LuCheck, LuInfo } from "react-icons/lu";

import { business, services } from "../content/site";

/** Fields the form collects. Six, deliberately - every extra field costs leads. */
const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  location: "",
  service: "",
  details: "",
};

/** Required fields, stated to the user above the form as well. */
const REQUIRED_FIELDS = ["name", "phone", "location", "details"];

/** Tab order, so focus can be moved to the FIRST invalid field. */
const FIELD_ORDER = ["name", "phone", "email", "location", "service", "details"];

const FIELD_LABELS = {
  name: "Name",
  phone: "Phone",
  email: "Email",
  location: "Service address or city",
  service: "Service needed",
  details: "Describe the problem",
};

/** Loose on purpose: a real number must never be rejected over formatting. */
const countDigits = (value) => value.replace(/\D/g, "").length;

const isPlausiblePhone = (value) => {
  const digits = countDigits(value);
  return digits >= 10 && digits <= 15;
};

const isPlausibleEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

/**
 * Validate the whole form on submit. Returns an object keyed by field name;
 * an empty object means valid.
 */
function validate(values) {
  const errors = {};

  REQUIRED_FIELDS.forEach((field) => {
    if (!values[field].trim()) {
      errors[field] = `${FIELD_LABELS[field]} is required.`;
    }
  });

  if (!errors.name && values.name.trim().length < 2) {
    errors.name = "Please enter your full name.";
  }

  if (!errors.phone && !isPlausiblePhone(values.phone)) {
    errors.phone =
      "Enter a phone number with at least 10 digits so we can call you back.";
  }

  // Email is optional, but if it is filled in it has to be usable.
  if (values.email.trim() && !isPlausibleEmail(values.email.trim())) {
    errors.email = "That email address does not look complete.";
  }

  if (!errors.details && values.details.trim().length < 10) {
    errors.details =
      "Add a sentence or two about what is happening so we bring the right parts.";
  }

  return errors;
}

/**
 * The Netlify form this posts to. Must match, exactly, three places:
 *   1. the hidden static form in public/index.html (build-time detection)
 *   2. the hidden `form-name` input rendered inside the live form below
 *   3. this constant
 * Change one and you must change all three, or submissions 404.
 */
export const NETLIFY_FORM_NAME = "contact";

/** Honeypot field name, declared via netlify-honeypot on the static form. */
export const HONEYPOT_FIELD = "bot-field";

/**
 * Lead delivery. Posts the form to Netlify Forms.
 *
 * Netlify intercepts a urlencoded POST to any path on the site and files it
 * against the form named by `form-name`. Posting to "/" is correct here even
 * with the SPA catch-all redirect in netlify.toml, because form handling runs
 * before redirect rules.
 *
 * Resolves when Netlify has accepted the submission, throws otherwise, so the
 * caller's error state is driven by a real server response rather than a guess.
 */
function deliverLead(values) {
  const serviceLabel =
    services.find((service) => service.slug === values.service)?.title ||
    "Other / not sure";

  const payload = {
    "form-name": NETLIFY_FORM_NAME,
    [HONEYPOT_FIELD]: values[HONEYPOT_FIELD] || "",
    name: values.name.trim(),
    phone: values.phone.trim(),
    email: values.email.trim(),
    location: values.location.trim(),
    // Send the human-readable service title, not the slug, so the
    // notification email is readable without a lookup.
    service: serviceLabel,
    details: values.details.trim(),
  };

  return fetch("/", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(payload).toString(),
  }).then((response) => {
    if (!response.ok) {
      throw new Error(
        `Netlify form submission failed with status ${response.status}`
      );
    }
    return response;
  });
}

/* Shared input styling. `text-base` is 16px so iOS never zooms on focus, and
   `min-h-tap` holds the 44px minimum target. `min-w-0` stops the browser's
   intrinsic ~20ch input width from widening the grid on a 280px screen. */
const FIELD_BASE =
  "w-full min-w-0 min-h-tap rounded-md border bg-surface px-3 py-3 text-base text-ink " +
  "placeholder:text-ink-subtle transition-colors duration-base ease-standard " +
  "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle";

const fieldClass = (hasError) =>
  `${FIELD_BASE} ${
    hasError
      ? "border-2 border-line-urgent hover:border-line-urgent"
      : "border-line hover:border-line-strong"
  }`;

const RequiredMark = () => (
  <span className="text-ink-brand" aria-hidden="true">
    {" "}
    *
  </span>
);

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} className="mt-2 flex items-start gap-2 text-body-sm text-ink">
      <LuInfo aria-hidden="true" className="mt-1 h-4 w-4 shrink-0" />
      <span>
        <span className="font-semibold">Error:</span> {message}
      </span>
    </p>
  ) : null;

const Contact = () => {
  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [announcement, setAnnouncement] = useState("");

  const fieldRefs = useRef({});
  const setFieldRef = (name) => (node) => {
    fieldRefs.current[name] = node;
  };

  const isSubmitting = status === "submitting";

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));

    // Clear a field's error as soon as the person starts fixing it.
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const nextErrors = validate(values);
    setErrors(nextErrors);

    const errorCount = Object.keys(nextErrors).length;
    if (errorCount > 0) {
      setStatus("error");
      setAnnouncement(
        `${errorCount} ${
          errorCount === 1 ? "field needs" : "fields need"
        } attention before this can be sent.`
      );

      const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field]);
      fieldRefs.current[firstInvalid]?.focus();
      return;
    }

    setStatus("submitting");
    setAnnouncement("Preparing your request.");

    try {
      await deliverLead(values);
      setStatus("success");
      setAnnouncement("Your request has been sent.");
    } catch {
      setStatus("error");
      setAnnouncement("Your request could not be sent. Please call us instead.");
      setErrors({
        form: "We could not send your request. Please call the number above and we will take the details over the phone.",
      });
    }
  };

  const resetForm = () => {
    setValues(EMPTY_FORM);
    setErrors({});
    setStatus("idle");
    setAnnouncement("");
    fieldRefs.current.name?.focus();
  };

  return (
    <section id="contact" className="section bg-surface-sunken">
      <div className="container-page">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-14">
          {/* ---------------------------------------------------------------
              Calling is the fast path. It leads, and it is visually loudest.
              --------------------------------------------------------------- */}
          <div className="min-w-0">
            <p className="kicker">Contact</p>
            <h2 className="mt-3 text-display text-ink">
              The fastest way to reach us is the phone.
            </h2>
            <p className="mt-4 max-w-prose text-body-lg text-ink-muted">
              Call and talk it through with someone who can schedule you. If it
              is not urgent, the form works too and we will follow up.
            </p>

            <a
              href={business.phone.href}
              className="btn-urgent mt-6 w-full text-body-lg sm:w-auto"
            >
              <LuPhone aria-hidden="true" className="h-5 w-5 shrink-0" />
              <span className="tabular">{business.phone.display}</span>
            </a>

            <p className="mt-5 flex flex-wrap items-center gap-2 text-body text-ink-muted">
              <LuMail aria-hidden="true" className="h-5 w-5 shrink-0" />
              <a
                href={business.email.href}
                className="text-link inline-flex min-h-tap items-center break-words py-2 [overflow-wrap:anywhere]"
              >
                {business.email.address}
              </a>
            </p>

            {/*
              Address and hours are `null` in src/content/site.js until the
              owner confirms them. Nothing is invented here; both blocks
              appear on their own once those fields are filled in.
            */}
            {business.address && (
              <p className="mt-4 text-body text-ink-muted">
                {business.address.street}, {business.address.city},{" "}
                {business.address.state} {business.address.zip}
              </p>
            )}
            {business.hours && (
              <p className="mt-2 text-body text-ink-muted">
                {business.hours.weekday}
              </p>
            )}
          </div>

          {/* ---------------------------------------------------------------
              The form: the alternative for non-urgent requests.
              --------------------------------------------------------------- */}
          <div className="min-w-0">
            {status === "success" ? (
              <div className="card p-6 sm:p-8" role="status">
                <p className="flex items-center gap-3 text-heading text-ink">
                  <LuCheck aria-hidden="true" className="h-6 w-6 shrink-0" />
                  Request received
                </p>
                <p className="mt-3 text-body text-ink-muted">
                  Thanks — we have your details and will be in touch. If it is
                  urgent, calling is still the fastest way to reach us.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={business.phone.href}
                    className="btn-urgent w-full sm:w-auto"
                  >
                    <LuPhone aria-hidden="true" className="h-5 w-5 shrink-0" />
                    <span className="tabular">{business.phone.display}</span>
                  </a>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-outline w-full sm:w-auto"
                  >
                    Send another request
                  </button>
                </div>
              </div>
            ) : (
              <form
                name={NETLIFY_FORM_NAME}
                method="POST"
                data-netlify="true"
                netlify-honeypot={HONEYPOT_FIELD}
                onSubmit={handleSubmit}
                noValidate
                aria-busy={isSubmitting}
                className="card p-6 sm:p-8"
              >
                {/* Netlify needs the form name in the POST body. */}
                <input
                  type="hidden"
                  name="form-name"
                  value={NETLIFY_FORM_NAME}
                />

                {/*
                  Honeypot. Hidden from people, irresistible to bots.
                  Not `type="hidden"` — bots skip those. It is a real text
                  input removed from layout, the tab order, and the a11y tree.
                */}
                <p className="hidden" aria-hidden="true">
                  <label>
                    Leave this field empty
                    <input
                      name={HONEYPOT_FIELD}
                      tabIndex={-1}
                      autoComplete="off"
                      value={values[HONEYPOT_FIELD] || ""}
                      onChange={(event) =>
                        setValues((prev) => ({
                          ...prev,
                          [HONEYPOT_FIELD]: event.target.value,
                        }))
                      }
                    />
                  </label>
                </p>

                <h3 className="text-heading text-ink">Request service</h3>
                <p className="mt-2 text-body-sm text-ink-muted">
                  Fields marked{" "}
                  <span className="font-semibold text-ink-brand">*</span> are
                  required: name, phone, service address, and a short
                  description.
                </p>

                {/* Error count / status announcements for screen readers. */}
                <p
                  aria-live="polite"
                  className={
                    announcement && status === "error"
                      ? "mt-4 rounded-md border-2 border-line-urgent bg-surface-urgent-soft px-4 py-3 text-body-sm text-ink"
                      : "mt-4 text-body-sm text-ink-muted empty:mt-0"
                  }
                >
                  {announcement}
                </p>

                {errors.form && (
                  <p className="mt-3 text-body-sm text-ink">{errors.form}</p>
                )}

                <div className="mt-5 grid gap-5">
                  {/* Name */}
                  <div className="grid content-start gap-2">
                    <label
                      htmlFor="contact-name"
                      className="relative text-body-sm font-semibold text-ink"
                    >
                      Name
                      <RequiredMark />
                      <span className="sr-only"> (required)</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      required
                      disabled={isSubmitting}
                      ref={setFieldRef("name")}
                      value={values.name}
                      onChange={handleChange}
                      aria-invalid={errors.name ? "true" : undefined}
                      aria-describedby={
                        errors.name ? "contact-name-error" : undefined
                      }
                      className={fieldClass(Boolean(errors.name))}
                    />
                    <FieldError id="contact-name-error" message={errors.name} />
                  </div>

                  {/* Phone */}
                  <div className="grid content-start gap-2">
                    <label
                      htmlFor="contact-phone"
                      className="relative text-body-sm font-semibold text-ink"
                    >
                      Phone
                      <RequiredMark />
                      <span className="sr-only"> (required)</span>
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      required
                      disabled={isSubmitting}
                      ref={setFieldRef("phone")}
                      value={values.phone}
                      onChange={handleChange}
                      aria-invalid={errors.phone ? "true" : undefined}
                      aria-describedby={
                        errors.phone ? "contact-phone-error" : undefined
                      }
                      className={`${fieldClass(Boolean(errors.phone))} tabular`}
                    />
                    <FieldError
                      id="contact-phone-error"
                      message={errors.phone}
                    />
                  </div>

                  {/* Email */}
                  <div className="grid content-start gap-2">
                    <label
                      htmlFor="contact-email"
                      className="relative text-body-sm font-semibold text-ink"
                    >
                      Email
                      <span className="font-normal text-ink-subtle">
                        {" "}
                        (optional)
                      </span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      disabled={isSubmitting}
                      ref={setFieldRef("email")}
                      value={values.email}
                      onChange={handleChange}
                      aria-invalid={errors.email ? "true" : undefined}
                      aria-describedby={
                        errors.email ? "contact-email-error" : undefined
                      }
                      className={`${fieldClass(
                        Boolean(errors.email)
                      )} [overflow-wrap:anywhere]`}
                    />
                    <FieldError
                      id="contact-email-error"
                      message={errors.email}
                    />
                  </div>

                  {/* Service address or city */}
                  <div className="grid content-start gap-2">
                    <label
                      htmlFor="contact-location"
                      className="relative text-body-sm font-semibold text-ink"
                    >
                      Service address or city
                      <RequiredMark />
                      <span className="sr-only"> (required)</span>
                    </label>
                    <input
                      id="contact-location"
                      name="location"
                      type="text"
                      autoComplete="street-address"
                      required
                      disabled={isSubmitting}
                      ref={setFieldRef("location")}
                      value={values.location}
                      onChange={handleChange}
                      aria-invalid={errors.location ? "true" : undefined}
                      aria-describedby={
                        errors.location
                          ? "contact-location-error contact-location-hint"
                          : "contact-location-hint"
                      }
                      className={fieldClass(Boolean(errors.location))}
                    />
                    <p
                      id="contact-location-hint"
                      className="text-body-sm text-ink-muted"
                    >
                      A city is enough if you would rather not share the full
                      address yet.
                    </p>
                    <FieldError
                      id="contact-location-error"
                      message={errors.location}
                    />
                  </div>

                  {/* Service needed */}
                  <div className="grid content-start gap-2">
                    <label
                      htmlFor="contact-service"
                      className="relative text-body-sm font-semibold text-ink"
                    >
                      Service needed
                    </label>
                    <select
                      id="contact-service"
                      name="service"
                      disabled={isSubmitting}
                      ref={setFieldRef("service")}
                      value={values.service}
                      onChange={handleChange}
                      className={fieldClass(false)}
                    >
                      <option value="">Other / not sure</option>
                      {services.map((service) => (
                        <option key={service.slug} value={service.slug}>
                          {service.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Describe the problem */}
                  <div className="grid content-start gap-2">
                    <label
                      htmlFor="contact-details"
                      className="relative text-body-sm font-semibold text-ink"
                    >
                      Describe the problem
                      <RequiredMark />
                      <span className="sr-only"> (required)</span>
                    </label>
                    <textarea
                      id="contact-details"
                      name="details"
                      rows={5}
                      required
                      disabled={isSubmitting}
                      ref={setFieldRef("details")}
                      value={values.details}
                      onChange={handleChange}
                      aria-invalid={errors.details ? "true" : undefined}
                      aria-describedby={
                        errors.details
                          ? "contact-details-error contact-details-hint"
                          : "contact-details-hint"
                      }
                      className={fieldClass(Boolean(errors.details))}
                    />
                    <p
                      id="contact-details-hint"
                      className="text-body-sm text-ink-muted"
                    >
                      What is happening, when it started, and anything that
                      smells or sparks.
                    </p>
                    <FieldError
                      id="contact-details-error"
                      message={errors.details}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary mt-6 w-full"
                >
                  {isSubmitting ? (
                    <>
                      <LuLoader
                        aria-hidden="true"
                        className="h-5 w-5 shrink-0 animate-spin motion-reduce:animate-none"
                      />
                      Sending...
                    </>
                  ) : (
                    "Send request"
                  )}
                </button>

                {/* Honest, minimal consent line. There is no privacy policy
                    page on this site, so none is linked. */}
                <p className="mt-4 text-body-sm text-ink-muted">
                  We use what you send here only to respond to this request and
                  schedule the work. We do not sell it or add you to a mailing
                  list.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
